import {useMemo} from 'react'
import PropTypes from 'prop-types'

import wordDiff, {canHighlight} from './lib/wordDiff'

// What to print before a token: its own separator (the new text's spacing,
// so paragraph breaks survive), a space when it has none but follows another
// token, nothing at the very start.
const separatorBefore = (token, index) => token.sep || (index > 0 ? ' ' : '')

/**
 * "Show changes" (TK-3204 AC 5.7): the answer with added words highlighted
 * and removed words struck through, in place of the textarea. Tokens render
 * as text nodes, never as markup. Each change carries a visually hidden
 * "Added:" / "Removed:" so a screen reader conveys what the highlight does.
 */
const RefineDiffView = ({before, after}) => {
  const tokens = useMemo(() => wordDiff(before, after), [before, after])
  const highlighted = useMemo(() => canHighlight(before, after), [before, after])

  return (
    <div className='ls-diff-wrap'>
      <div aria-label='Changes from the previous version' className='ls-diff-view' role='region'>
        {
          tokens.map((token, index) => {
            const separator = separatorBefore(token, index)

            if (token.type === 'ins')
              return <span key={index}>{separator}<ins><span className='sr-only'>Added: </span>{token.text}</ins></span>

            if (token.type === 'del')
              return <span key={index}>{separator}<del><span className='sr-only'>Removed: </span>{token.text}</del></span>

            return <span key={index}>{separator}{token.text}</span>
          })
        }
      </div>
      <div className='ls-diff-legend'>
        {
          highlighted ?
            <>
              <span><ins>Added</ins></span>
              <span><del>Removed</del></span>
            </> :
            <span>This story is too long to highlight word by word, so this is the new version as it reads.</span>
        }
      </div>
    </div>
  )
}

RefineDiffView.propTypes = {
  after: PropTypes.string.isRequired,
  before: PropTypes.string.isRequired
}

export default RefineDiffView
